import { Alert, Box, Button, Grid, SelectChangeEvent, SvgIcon, Typography, useMediaQuery } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import useNswagClient from '../../hooks/api/useNswagClient';
import useLogError from '../../hooks/useLogError';
import { LocationResponse, StockCheckDetails, StockCheckItem, StockCheckStatus } from '../../app/services/api/generated';
import StockCountDetails from './components/StockCountDetails';
import { Save01, Play, Camera01, FileDownload02 } from '../../assets';
import { StockCheckStatus as StockCheckStatusEnum } from '../../enums/StockCheckStatus';
import LoadingWheel from '../../components/ui/LoadingWheel';
import SubmitModal from './components/SubmitModal';
import { theme } from '../../theme';
import { UserContext } from '../../components/shared/useUser';
import PurchasedOrdersModal from './modals/PurchasedOrdersModal';
import LeavePageModal from '../../components/shared/dialogs/LeavePageModal';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import BackButton from '../../components/shared/actions/BackButton';
import { ConfigurationName } from '../../enums/ConfigurationName';
import { exportFile } from '../../utils';
import { Save } from '@mui/icons-material';
import PrintStockCountModal from './modals/PrintStockCountModal';
import ConfirmZoneSwitchModal from './modals/ConfirmZoneSwitchModal';

const StockCountDetailsPage = () => {
  const navigate = useNavigate();
  const { getStockCheckDetails, getPurchaseOrders, saveStockCheck, submitStockCheck, startStockCheck, getStoreLocations, getStockCheckByIdAndZoneId, saveStockCheckAtLocation, submitStockCheckAtLocation, getConfiguration, downloadScanAuditReport } = useNswagClient();
  const [stockCheckData, setStockCheckData] = useState<StockCheckDetails>({});
  const { logError } = useLogError();
  const [loading, setLoading] = useState<boolean>(false);
  const { id } = useParams<{ id: string | undefined }>();
  const stockCheckId = id ? parseInt(id) : undefined;
  const [showSavedAlert, setShowSavedAlert] = useState<boolean>(false);
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
  const [countComplete, setCountComplete] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState(false);
  const [countDate, setCountDate] = useState<string | undefined>('');
  const [openPurchaseModal, setOpenPurchaseModal] = useState(false);
  const [disableCount, setDisableCount] = useState(false);
  const [isScannerModalOpen, setIsScannerModalOpen] = useState<boolean>(false);
  const [zonesData, setZonesData] = useState<LocationResponse[]>([]);
  const [selectedZoneId, setSelectedZoneId] = useState<string>('-1');
  const [allZonesEnabled, setAllZonesEnabled] = useState<boolean>(true);
  const [allZonesStockCheckItems, setAllZonesStockCheckItems] = useState<StockCheckItem[]>([]);
  const childRef = useRef<{ getUpdatedValues: () => { scheduledDate: string, stockCheckItems: StockCheckItem[] } | null} >(null);
  const { t } = useTranslation('common');
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [isOnline, setIsOnline] = useState(true);
  const [nextPathname, setNextPathname] = useState('');
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const [saveReorder, setSaveReorder] = useState(false);
  const [hasBarcodeScanConfig, setHasBarcodeScanConfig] = useState(false);
  const canDownloadScanAudit = hasBarcodeScanConfig && hasPermissionTo(['BarcodeManagementAudit']);
  const [showAutosaveMessage, setShowAutosaveMessage] = useState<boolean>(false);
  const [modifiedItems, setModifiedItems] = useState<Set<number>>(new Set());
  const [navigateToIndex, setNavigateToIndex] = useState<boolean>(false);
  const [showExitAndSavedAlert, setShowExitAndSavedAlert] = useState(false);
  const [isExitAndSavedClicked, setIsExitAndSavedClicked] = useState(false);
  const [sortState, setSortState] = useState<boolean | undefined>(false);
  const [itemScanned, setItemScanned] = useState<boolean>(false);
  const [openPrintModal, setOpenPrintModal] = useState<boolean>(false);
  const [saveOngoing, setSaveOngoing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedReorder, setHasUnsavedReorder] = useState(false);
  const [showStartCountWarning, setShowStartCountWarning] = useState(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      const newPathname = nextLocation.pathname;

      // Check if there are unsaved changes or if the user is offline
      const hasUnsavedChanges = modifiedItems.size > 0 && !isExitAndSavedClicked;
      const shouldBlock = hasUnsavedChanges || (!isOnline && currentLocation.pathname !== newPathname);

      if (shouldBlock) {
        setNextPathname(newPathname);
      }

      return shouldBlock;
    },
  );

  useEffect(() => {
    const handleBeforeUnload = (event: { preventDefault: () => void; returnValue: string; }) => {
      if (!isOnline || modifiedItems.size > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [navigator.onLine, modifiedItems]);


  useEffect(() => {
    const handleOnlineStatusChange = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);

    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, [navigator.onLine]);

  useEffect(() => {
    if (navigateToIndex) {
      navigate('/store/perform-stock-count');
      if (blocker?.state === 'blocked') {
        blocker.proceed();
      }
      setNavigateToIndex(false);
    }
  }, [navigateToIndex, blocker]);

  const handleSaveLeavePageModalCancel = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const handleSaveLeavePageModalConfirm = () => {
    handleSave();
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };


  useEffect(() => {
    setLoading(true);
    GetStockCheckDetails(stockCheckId);
    if (selectedStore?.storeNumber) {
      loadZones(selectedStore?.storeNumber);
    }
  }, [countComplete]);

  useEffect(() => {
    if (stockCheckData.stockCheck?.status === StockCheckStatusEnum.Counting) {
      setSortState(stockCheckData.stockCheck?.reorderEnabled);
    }
  }, []);
 
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getConfiguration(ConfigurationName.BarcodeScanning, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '').then((res: any) => {
      if (res.data.value === 'true') {
        setHasBarcodeScanConfig(true);
      }
    });
  }, []);

  // Callback to receive modifiedItems from child
  const handleModifiedItemsChange = (newModifiedItems: Set<number>) => {
    setModifiedItems(newModifiedItems);
  };


  const formatQuantToDecimal = (result: StockCheckItem[]): StockCheckItem[] => {
    const items = result.map(a => {
      a.itemValues = a.itemValues?.map(x => {
        let finalValue: string | number | undefined = x.countQuantity;

        if (a.isAllowPartialUomTransactions && x.conversionToPrimary === 1) {
          const strVal = x.countQuantity?.toString(); // ensure it's a string
          const formattedValue = strVal?.split('.').slice(0, 2).join('.');
          const [integerPart, decimalPart = ''] = (formattedValue ?? '').split('.');

          finalValue = decimalPart.length > 0
            ? `${integerPart}.${decimalPart.padEnd(2, '0').slice(0, 2)}`
            : `${integerPart}.00`;

          if (integerPart === '') {
            finalValue = '';
          }
        }

        return {
          ...x,
          countQuantity: finalValue as number,
        };
      });

      return {
        ...a,
      };
    });

    return items;
  };

  const GetStockCheckDetails = (stockCheckId: number | undefined) => {
    setLoading(true);

    setSaveReorder(false);
    getStockCheckDetails(stockCheckId).then((result) => {
      if (result?.data) {
        if (result?.data.stockCheck?.status === StockCheckStatusEnum.PendingApproval ||
          result?.data.stockCheck?.status === StockCheckStatusEnum.Completed) {
          navigate(`/store/review-stock-count/${stockCheckId}`);
          setLoading(false);
          return;
        }

        if (!result?.data.stockCheck?.canStartCount && result?.data.stockCheck?.status === StockCheckStatusEnum.Pending) {
          setShowErrorAlert(true);
          setCountDate(result.data.stockCheck.unlockTimeUtc);
          setDisableCount(true);
        }

        if (result.data.stockCheck) {
          result.data.stockCheck.items = formatQuantToDecimal(result.data.stockCheck?.items ?? []);
        }

        setStockCheckData(result.data);
        setAllZonesStockCheckItems(result.data.stockCheck?.items ?? []);
        if( !hasPermissionTo(['CountItemReorderingWrite'])){
          setSortState(true);
        }


        setAllZonesEnabled(result.data.allCountZonesEnabled ?? true);
        setLoading(false);
      }
    }).catch((error) => {
      logError(error);
    });

    setSaveOngoing(false);
  };

  const loadZones = (storeNumber: string) => {
    getStoreLocations(storeNumber).then((result) => {
      if (result?.data) {
        setZonesData(result.data);
      }
    })
      .catch((error) => {
        logError(`Error fetching zones data:', ${error}`);
      });
  };

  const isStockPending = stockCheckData?.stockCheck?.status === StockCheckStatusEnum.Pending;
  const isStockCounting = stockCheckData?.stockCheck?.status === StockCheckStatusEnum.Counting;

  const handleCount = () => {
    setOpenPurchaseModal(false);
    setHasUnsavedReorder(false);
    const stockId = stockCheckId ?? 0;
    const currentReorderState = sortState;
    setLoading(true);
    startStockCheck(stockId, currentReorderState).then(() => {
      setLoading(false);
      setCountComplete(true);
    })
      .catch((error) => {
        logError(error);
      });
  };

  const handleStockReorderSave = () => {
    setSaveReorder(true);
  };

  useEffect(() => {
    if (saveOngoing) {
      setSortState(false);

      GetStockCheckDetails(stockCheckId);
    }
  }, [saveOngoing]);

  const handleClose = () => {
    setOpenPurchaseModal(false);
    setOpenPrintModal(false);
  };
  const startCount = () => {
    if (hasUnsavedReorder) {
      setShowStartCountWarning(true);
      return;
    }
    proceedStartCount();
  };

  const confirmStartCounting = () => {
    setShowStartCountWarning(false);
    proceedStartCount();
  };

  const cancelStartCounting = () => {
    setShowStartCountWarning(false);
  };

  const handleReorderStateChange = (hasUnsaved: boolean) => {
    setHasUnsavedReorder(hasUnsaved);
  };

  const proceedStartCount = () => {
    const storeNumber = selectedStore?.storeNumber;
    getPurchaseOrders(storeNumber, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, false, undefined, undefined, undefined, 10).then((result) => {
      if (result?.data?.purchaseOrderHeadersCount! > 0) {
        setOpenPurchaseModal(true);
      } else {
        setLoading(true);
        handleCount();
      }
    })
      .catch((error) => {
        logError(error);
      });
  };

  const getStatusLabel = (status: StockCheckStatus | undefined): string => {

    switch (status) {
    case StockCheckStatusEnum.Counting:
      return t('stockCountStatus.counting');
    case StockCheckStatusEnum.Completed:
      return t('stockCountStatus.completed');
    case StockCheckStatusEnum.Pending:
      return t('stockCountStatus.pending');
    case StockCheckStatusEnum.PendingApproval:
      return t('stockCountStatus.pendingApproval');
    default:
      return t('stockCountStatus.unknownStatus');
    }
  };

  const closeModal = () => {
    setIsScannerModalOpen(false);
    if (stockCheckData.stockCheck?.reorderEnabled) {
      setItemScanned((prevState) => !prevState);
    }
  };

  const handleScannerClick = () => {
    setIsScannerModalOpen(true);
  };
  const formatCountDate = (inputString: string) => {
    const inputDate = new Date(inputString);

    const countDate = `${('0' + inputDate.getDate()).slice(-2)}-${(
      '0' +
      (inputDate.getMonth() + 1)
    ).slice(-2)}-${inputDate.getFullYear()} ${('0' + inputDate.getHours()).slice(
      -2,
    )}:${('0' + inputDate.getMinutes()).slice(-2)}`;

    return countDate;
  };
  const handleSave = async(isOnExit: boolean = false): Promise<boolean> => {
    if (isSaving) { return false; }
    setIsSaving(true);
    let isSaved = false;

    try {
      const { scheduledDate, stockCheckItems } = childRef.current?.getUpdatedValues() ?? {};

      // Update the stockCheckData with the new values
      const updatedStockCheckData: StockCheckDetails = {
        ...stockCheckData,
        stockCheck: {
          ...stockCheckData.stockCheck,
          plannedCountDate: (scheduledDate),
          items: stockCheckItems,
        },
      };

      if(selectedZoneId == '-1'){
        await saveStockCheck(stockCheckId ?? 0, stockCheckItems);
        setModifiedItems(new Set());
        setStockCheckData(updatedStockCheckData);
        isOnExit ? setShowExitAndSavedAlert(true) : setShowSavedAlert(true);
        isSaved = true;
      }
      else {
        await saveStockCheckAtLocation(stockCheckId ?? 0, parseInt(selectedZoneId), stockCheckItems);
        setModifiedItems(new Set());
        setStockCheckData(updatedStockCheckData);
        isOnExit ? setShowExitAndSavedAlert(true) : setShowSavedAlert(true);
        isSaved = true;
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsSaving(false);
    }

    return isSaved;
  };

  const handleExitAndSave = () => {
    setIsExitAndSavedClicked(true);
    handleSave(true);
    setTimeout(() => {
      navigate('/store/stock-count');
    }, 2000);
  };
  const closeExitAndSaveAlert = () => {
    setShowExitAndSavedAlert(false);
  };

  const handleConfirmModal = async () => {
    if (isSubmitting) { return; }
    setIsSubmitting(true);
    setLoading(true);

    try {
      const { stockCheckItems } = childRef.current?.getUpdatedValues() ?? {};

      if (selectedZoneId === '-1') {
        await submitStockCheck(stockCheckId ?? 0, stockCheckItems)
          .then(() => {
            setOpenModal(false);
            setLoading(false);
            navigate('/store/stock-count');
          })
          .catch((error) => {
            logError(error);
          });
      }
      else {
        await submitStockCheckAtLocation(stockCheckId ?? 0, parseInt(selectedZoneId), stockCheckItems)
          .then(() => {
            setOpenModal(false);
            setLoading(false);
            navigate('/store/stock-count');
          })
          .catch((error) => {
            logError(error);
          });
      }
    } catch (error) {
      logError(error);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleCancelModal = () => {
    setOpenModal(false);
  };

  const submitButtonHandler = () => {
    setOpenModal(true);
  };
  const updateSortState = () => {
    setSortState((prevState) => !prevState);

  };

  const GetStockCheckByIdAndZoneId = (stockCheckId: number, zoneId: number) => {
    setLoading(true);
    getStockCheckByIdAndZoneId(stockCheckId, zoneId).then((result) => {
      if (result?.data) {
        if (!result?.data.stockCheck?.canStartCount && result?.data.stockCheck?.status === StockCheckStatusEnum.Pending) {
          setShowErrorAlert(true);
          setCountDate(result.data.stockCheck.unlockTimeUtc);
          setDisableCount(true);

        }
        if (result.data.stockCheck) {
          result.data.stockCheck.items = formatQuantToDecimal(result.data.stockCheck?.items ?? []);
        }
        setStockCheckData(result.data);
        setAllZonesEnabled(result.data.allCountZonesEnabled ?? true);
        setLoading(false);
      }
    })
      .catch((error) => {
        logError(error);
      });
  };

  const handleSelectedZoneChange = async(event: SelectChangeEvent<string>) => {
    if(!isOnline){
      setShowLocationWarning(true);
      return;
    }

    const newValue = event.target.value;

    if (newValue === selectedZoneId) {
      return;
    }

    if (modifiedItems.size > 0) {
      const saveSuccess = await handleSave();
      if (!saveSuccess) {
        return;
      }
    }

    setShowSavedAlert(false);
    setSelectedZoneId(newValue);
    if (newValue != '-1') {
      GetStockCheckByIdAndZoneId(stockCheckId ?? 0, parseInt(newValue));
    }
    else {
      GetStockCheckDetails(stockCheckId);
    }
  };

  const handleSavingDataUserBackOnline = () => {
    handleSave();
  };
  const handleCloseSaveAlert = () => {
    setShowSavedAlert(false);
  };

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const onCancelLocationWarning = () => {
    setShowLocationWarning(false);
  };

  const handleAutosaveMessage = (changesSaved: boolean) => {
    setShowAutosaveMessage(changesSaved);
  };

  const canReorder = () => {
    if (isStockCounting) {
      return stockCheckData.stockCheck?.reorderEnabled && hasPermissionTo(['CountItemReorderingWrite']);
    }
    return sortState && hasPermissionTo(['CountItemReorderingWrite']);
  };

  const isSortingEnabled = () => {
    if(isStockCounting){
      return stockCheckData.stockCheck?.reorderEnabled;
    }
    return sortState;
  };

  
  const [printAll,setPrintAll] = useState<boolean>(false);

  const handleModalOpen = () => {
    setPrintAll(false);
    setOpenPrintModal(true);
  };

  const handlePrintAll = () => {
    setPrintAll(true);
    setOpenPrintModal(true);

  };

  const handlePrintAllforCounting = () => {
    setPrintAll(true);

    handlePrinting(true);
  };

  const handlePrint = () => {
    handlePrinting(printAll);
  };

  const handlePrinting = (printAllValue: boolean) => {
    setOpenPrintModal(false);
    const styleTag = document.createElement('style');
    if (printAllValue) {
      // Defined css to not take the content of the UI
      styleTag.innerHTML = `
      @media print {
        .stock-count-categories { display: none !important; }
        #print-section { display: block !important; }
        .filtered-data-table { display: none !important; }
      }
    `;
    }

    document.head.appendChild(styleTag);
    setTimeout(() => {
      window.print();
      document.head.removeChild(styleTag);
    }, 300);
    setTimeout(() => {
      setPrintAll(false);
    }, 5000); // Adjust timing as needed
  };


  const handleDownloadScanAuditReport = (id: number) => {
    const isSafari =
        /^(?!chrome|android).*safari/i.test(navigator.userAgent);

    const safariTab = isSafari
      ? window.open('', '_blank')
      : null;

    downloadScanAuditReport(id)
      .then((response) => {
        if (response) {
          if (isSafari && safariTab) {
            const blob = new Blob([response.data], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            const fileUrl = URL.createObjectURL(blob);
            safariTab.location.href = fileUrl;

            setTimeout(
              () => URL.revokeObjectURL(fileUrl),
              60_000,
            );

            return;
          }

          exportFile(
            response.data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'ScanAuditReport.xlsx',
          );
        } else {
          safariTab?.close();
        }
      })
      .catch((error) => {
        safariTab?.close();
        logError(error);
      });
  };

  return (
    <Box display="flex"
      width="100%"
      height="100%"
      flexDirection="column" >
      <PageArea>
        <Grid item
          xs={12}
          className={'navigation-holder'}
          sx={{ textAlign: 'left' }}>
          {isStockPending && (
            <BackButton
              url="/store/stock-count"
              title={t('back')}
              isLink
            />
          )}
          {isStockCounting && (
            <BackButton
              handleClick={handleExitAndSave}
              title={t('exitAndSave')}
              isLink={false}
              styles={{
                color: isOnline ? theme.palette.primary[500] : theme.palette.custom.gray[500],
                cursor: isOnline ? 'auto' : 'none',
              }}
            />
          )}
        </Grid>
        <Grid>
          <PageTitle>
            {t('stockCount')}: {stockCheckData?.stockCheck?.name}
          </PageTitle>
        </Grid>
        <Grid item
          xs={12}
          sx={{ textAlign: 'left' }}>
          <Typography variant="textSM">Status: {getStatusLabel(stockCheckData?.stockCheck?.status)}</Typography>
        </Grid>
        {isStockPending && (
          <Grid item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {zonesData.length > 0 && (
              <Button 
                onClick={handlePrintAll} 
                variant="primary" 
                size="lg"
                sx={{ mb: 4 }}
              >
                {t('printAll')}
              </Button>
            )}
            {canReorder()
              && (<Grid item>
                <Button
                  variant="primary"
                  size="lg"
                  sx={{ ml: 4, mb: 4 }}
                  onClick={handleStockReorderSave}
                  startIcon={<SvgIcon> <Save fill='white' /> </SvgIcon>}
                >
                  {t('saveReorder')}
                </Button>
              </Grid>
              )}

          </Grid>
        )}

        {isStockPending &&
          (<Grid item
            xs={12}
            sx={{ textAlign: 'right' }}>
            <Button 
              onClick={canReorder() ? handleModalOpen : () => window.print()} 
              variant="primary" 
              size="lg"
              sx={{ mb: 4 }}
            >
              {t('printCurrent')}
            </Button>

            <Button variant="primary"
              size="lg"
              sx={{ ml: 4, mb: 4 }}
              disabled={disableCount || !hasPermissionTo(['StockCountWrite'])}
              onClick={startCount}
              startIcon={<SvgIcon> <Play fill='white' /> </SvgIcon>}
            >
              {t('startCounting')}
            </Button>
          </Grid>)
        }
        {isStockCounting && (
          <Grid item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => window.print()}
              variant="primary"
              size="lg"
              sx={{ mb: 4 }}>{t('printCurrent')}</Button>

            {zonesData.length > 0 && <Button onClick={handlePrintAllforCounting}
              variant="primary"
              size="lg"
              sx={{ ml: 4, mb: 4 }}>{t('printAll')}</Button>}

          </Grid>
        )}
        {isStockCounting && (
          <>
            <Grid container
              sx={{ mt: 2 }}
              justifyContent="flex-end">
              <Grid item
                xs={12}
                sx={{ textAlign: 'right' }}>

                {isMobile && hasBarcodeScanConfig && <Button
                  variant="primary"
                  size="lg"
                  sx={{ mb: 4 }}
                  startIcon={<SvgIcon> <Camera01 /> </SvgIcon>}
                  onClick={handleScannerClick}
                >
                  {t('stockCountPage.barcodeScanning')}
                </Button>}

                <Button
                  variant="primary"
                  size="lg"
                  startIcon={<SvgIcon fill='white'> <Save01 fill='white' /> </SvgIcon>}
                  sx={{ ml: 4, mb: 4 }}
                  disabled={!isOnline || isSaving}
                  onClick={()=> handleSave()}
                >
                  {t('save')}
                </Button>

                <Button
                  variant="primary"
                  size="lg"
                  type='submit'
                  sx={{ ml: 4, mb: 4 }}
                  disabled={!isOnline || isSubmitting}

                  onClick={submitButtonHandler}
                >
                  {t('submit')}
                </Button>
                {canDownloadScanAudit && <Button
                  variant="primary"
                  size="lg"
                  startIcon={<SvgIcon fill='white'> <FileDownload02 /> </SvgIcon>}
                  sx={{ ml: 4, mb: 4 }}
                  disabled={isStockPending}
                  onClick={() => handleDownloadScanAuditReport(stockCheckData?.stockCheck?.id ?? 0)}
                >
                  {t('stockcounts.exportScanAudit')}
                </Button>}
              </Grid>
              <Grid item
                xs={12}
                sx={{ textAlign: 'right' }}>
                <Typography variant='textLG'
                  sx={{
                    visibility: !showSavedAlert && showAutosaveMessage ? 'visible' : 'hidden',
                  }}
                >
                  {t('changesSaved')}
                </Typography>
              </Grid>
            </Grid>

            {showSavedAlert &&
              <Grid item
                xs={12}
                sx={{ pt: '20px' }}>
                <Alert severity="success"
                  onClose={handleCloseSaveAlert}>{t('stockSaved')}</Alert>

              </Grid>
            }
            <SubmitModal openModal={openModal}
              onConfirm={handleConfirmModal}
              onCancel={handleCancelModal}></SubmitModal>
          </>
        )}
        {showErrorAlert && (
          <Grid container
          >
            <Grid item
              xs={12}
              sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
              <Alert
                severity="error">{t('countInfo')} {formatCountDate(countDate!)} </Alert>
            </Grid>
          </Grid>
        )}
        <Grid item
          xs={12}
          sx={{ pt: '20px' }}>
          {loading ? <LoadingWheel />
            : (isStockCounting || isStockPending) &&
            <StockCountDetails stockCheckDetails={stockCheckData}
              isScannerModalOpen={isScannerModalOpen}
              openScannerModal={handleScannerClick}
              closeModal={closeModal}
              zonesData={zonesData}
              allZonesStockCheckItems={allZonesStockCheckItems}
              saveReorder={saveReorder}
              setSaveReorder={setSaveReorder}
              sortState={sortState}
              itemScanned={itemScanned}
              printAll={printAll}
              handleSortStateChange={updateSortState}
              setSaveOngoing={setSaveOngoing}
              selectedZoneId={selectedZoneId}
              handleSelectedZoneChange={handleSelectedZoneChange}
              allZonesEnabled={allZonesEnabled}
              handleSavingDataUserBackOnline={handleSavingDataUserBackOnline}
              handleAutosaveMessage={handleAutosaveMessage}
              handleCloseSaveAlert={handleCloseSaveAlert}
              showSavedAlert={showSavedAlert}
              onModifiedItemsChange={handleModifiedItemsChange}
              isBlockerActive={blocker.state === 'blocked'}
              onReorderStateChange={handleReorderStateChange}
              ref={childRef}
              hasBarcodeScanningPermission={hasBarcodeScanConfig}
            ></StockCountDetails>
          }
        </Grid>
      </PageArea>
      <PurchasedOrdersModal
        open={openPurchaseModal}
        onClose={handleClose}
        onCancel={handleCount}
      />
      <LeavePageModal
        open={blocker.state === 'blocked' && !isOnline}
        onConfirm={handleLeavePageModalConfirm}
        onClose={handleLeavePageModalClose}
        onConfirmUrl={nextPathname}
        leaveWarningContent={t('stockcounts.warningOfflineMessage')}
      />
      <MessagesSnackbar
        open={showLocationWarning && !isOnline}
        onClose={onCancelLocationWarning}
        message={t('stockcounts.warningOfflineLocationSwitch')}
        severity="warning"
        verticalPosition="top"
        duration={null}
      />
      <LeavePageModal
        open={isOnline && blocker.state === 'blocked'}
        onConfirm={handleSaveLeavePageModalConfirm}
        onClose={handleLeavePageModalClose}
        onCancel={handleSaveLeavePageModalCancel}
        onConfirmUrl='/store/perform-stock-count'
        leaveWarningContent={t('saveOnLeaveWarningContent')}
        leaveWarningTitle={t('saveOnLeaveWarningTitle')}
      />
      <MessagesSnackbar
        open={showExitAndSavedAlert}
        onClose={closeExitAndSaveAlert}
        message={t('changesSaved')}
        severity="success"
        duration={5000} />

      <PrintStockCountModal
        open={openPrintModal}
        onClose={handleClose}
        onConfirm={handlePrint} />
      <ConfirmZoneSwitchModal
        open={showStartCountWarning}
        onClose={cancelStartCounting}
        onConfirm={confirmStartCounting}
        titleKey="confirmStartCounting"
        warningKey="startCountingWarning"
      />
    </Box>
  );
};

export default StockCountDetailsPage;